import { forwardRef } from 'react';
import type { IconType } from 'react-icons';

import type {
  PolymorphicForwardRefComponentV2,
  SvgIcon,
} from '../../utils/types';

export type ButtonBaseOwnProps = {
  size?: 's' | 'm' | 'l';
  /**
   * Buttonのヴィジュアルスタイルを指定します
   * - primary: 塗りつぶし
   * - secondary: アウトライン
   * - tertiary: ゴースト
   */
  variant?: 'primary' | 'secondary' | 'tertiary';
  /**
   * Buttonのベースカラーを指定します
   * - positive: blue
   * - negative: grey
   * - danger: red
   * - apply: orange
   */
  subVariant?: 'positive' | 'negative' | 'danger' | 'apply';
  /** `true` の場合、 width: 100% を適用します */
  fullWidth?: boolean;
  /** アクション内容を補足するためのアイコンをボタンテキストの先頭に設置します */
  startIcon?: IconType | SvgIcon;
  /** アクション後の遷移先を説明するためのアイコンをボタンテキストの末尾に設置します */
  endIcon?: IconType | SvgIcon;
  /**
   * - IconButton としてボタンを描画するモードです
   * - 描画されるアイコンの優先順位は、`icon` > `startIcon` > `children` です
   * - endIconは描画されません
   */
  isIcon?: boolean;
  /** IconButton としてのアイコンを指定します */
  icon?: IconType | SvgIcon;
};

export const ButtonBase = forwardRef(
  (
    {
      children,
      as = 'button',
      startIcon: StartIcon,
      endIcon: EndIcon,
      type,
      size,
      variant,
      subVariant,
      fullWidth,
      isIcon,
      icon: Icon,
      ...rest
    },
    forwardedRef
  ) => {
    const Root = as;

    return (
      <Root
        data-full-width={fullWidth || undefined}
        data-is-icon={isIcon || undefined}
        data-size={size || 'm'}
        data-sub-variant={subVariant || 'positive'}
        data-variant={variant || 'primary'}
        ref={forwardedRef}
        // @ts-expect-error props.type が HTMLAnchorElementの type: string | undefined にワイドニングされる
        type={as === 'button' && type === undefined ? 'button' : type}
        {...rest}
      >
        {isIcon ? (
          Icon ? (
            <Icon aria-hidden />
          ) : StartIcon ? (
            <StartIcon aria-hidden />
          ) : (
            children
          )
        ) : (
          <>
            {StartIcon && <StartIcon aria-hidden />}
            {children}
            {EndIcon && <EndIcon aria-hidden />}
          </>
        )}
      </Root>
    );
  }
) as PolymorphicForwardRefComponentV2<
  'button',
  'button' | 'a',
  ButtonBaseOwnProps
>;
ButtonBase.displayName = 'ButtonBase';
